import React, { useEffect, useMemo, useState } from "react";
import "./CareersOpenPositions.css";
import { FiSearch } from "react-icons/fi";
import { FiChevronDown } from "react-icons/fi";
import { FiArrowDown } from "react-icons/fi";
import { useNavigate } from "react-router-dom";
import { getCareerJobs } from "../../../../api/careers";
import NoJobs from "./NoJobs";

const ITEMS_PER_PAGE = 5;

const CareersOpenPositions = () => {
  const navigate = useNavigate();
  const [search, setSearch] = useState("");
  const [selectedDepartment, setSelectedDepartment] = useState("All");
  const [sortBy, setSortBy] = useState("Latest");
  const [visibleCount, setVisibleCount] = useState(ITEMS_PER_PAGE);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [apiJobs, setApiJobs] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    let isMounted = true;
    const load = async () => {
      setLoading(true);
      try {
        const data = await getCareerJobs();
        if (!isMounted) return;
        const normalized = (Array.isArray(data) ? data : []).map((job, idx) => ({
          ...job,
          id: job?.jobKey || `job-${job?.id || idx + 1}`,
          type: job?.employmentType || "Full time",
          posted: job?.createdAtUtc ? `Posted ${new Date(job.createdAtUtc).toLocaleDateString()}` : "Posted recently",
          order: Number(job?.displayOrder || idx + 1),
        }));
        setApiJobs(normalized);
      } catch {
        if (isMounted) setApiJobs([]);
      } finally {
        if (isMounted) setLoading(false);
      }
    };
    load();
    return () => {
      isMounted = false;
    };
  }, []);

  const jobsSource = apiJobs;

  const departmentOptions = useMemo(
    () => ["All", ...new Set(jobsSource.map((job) => job.department).filter(Boolean))],
    [jobsSource]
  );

  const filteredJobs = useMemo(() => {
    const searchedJobs = jobsSource.filter((job) =>
      job.title.toLowerCase().includes(search.toLowerCase())
    );

    const departmentFilteredJobs =
      selectedDepartment === "All"
        ? searchedJobs
        : searchedJobs.filter((job) => job.department === selectedDepartment);

    const sortedJobs = [...departmentFilteredJobs].sort((a, b) => {
      return sortBy === "Latest" ? a.order - b.order : b.order - a.order;
    });

    return sortedJobs;
  }, [jobsSource, search, selectedDepartment, sortBy]);

  const visibleJobs = filteredJobs.slice(0, visibleCount);
  const canLoadMore = visibleCount < filteredJobs.length;

  const handleLoadMore = () => {
    if (isLoadingMore || !canLoadMore) return;
    setIsLoadingMore(true);
    window.setTimeout(() => {
      setVisibleCount((prev) => prev + ITEMS_PER_PAGE);
      setIsLoadingMore(false);
    }, 900);
  };

  const handleSearchChange = (e) => {
    setSearch(e.target.value);
    setVisibleCount(ITEMS_PER_PAGE);
  };

  const handleDepartmentChange = (e) => {
    setSelectedDepartment(e.target.value);
    setVisibleCount(ITEMS_PER_PAGE);
  };

  const handleSortChange = (e) => {
    setSortBy(e.target.value);
    setVisibleCount(ITEMS_PER_PAGE);
  };

  if (!loading && jobsSource.length === 0) {
    return <NoJobs />;
  }

  return (
    <section className="careers-open">
      <div className="careers-open-container">
        
        <h2 className="open-title">Open roles</h2>

        {/* FILTER BAR */}
        <div className="open-filters">
          <span className="role-label">Role</span>

          <div className="filters-right">

            {/* SEARCH */}
            <div className="search-box">
              <input
                type="text"
                placeholder="Search by role or keywords"
                value={search}
                onChange={handleSearchChange}
              />
              <FiSearch />
            </div>

            {/* DEPARTMENT */}
            <div className="filter-btn">
              <span>Department:</span>
              <select
                value={selectedDepartment}
                onChange={handleDepartmentChange}
                aria-label="Filter by department"
              >
                {departmentOptions.map((option) => (
                  <option value={option} key={option}>
                    {option}
                  </option>
                ))}
              </select>
              <FiChevronDown />
            </div>

            {/* SORT */}
            <div className="filter-btn">
              <FiArrowDown />
              <span>Sort by:</span>
              <select
                value={sortBy}
                onChange={handleSortChange}
                aria-label="Sort jobs"
              >
                <option value="Latest">Latest</option>
                <option value="Oldest">Oldest</option>
              </select>
            </div>

          </div>
        </div>

        {/* LIST */}
        <div className="jobs-list">
          {loading && !visibleJobs.length ? (
            <div className="job-row">
              <div className="job-left"><h3>Loading jobs...</h3></div>
            </div>
          ) : null}
          {!loading && jobsSource.length > 0 && !visibleJobs.length ? (
            <div className="job-row">
              <div className="job-left"><h3>No matching roles found.</h3></div>
            </div>
          ) : null}
          {visibleJobs.map((job) => (
            <div
              className="job-row"
              key={job.id}
              onClick={() => navigate(`/careers/${job.jobKey || job.id}`)}
              role="button"
              tabIndex={0}
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") {
                  e.preventDefault();
                  navigate(`/careers/${job.jobKey || job.id}`);
                }
              }}
            >
              
              <div className="job-left">
                <h3>{job.title}</h3>
                <p>
                  {job.type} • {job.department}
                </p>
              </div>

              <div className="job-mid">
                <span>{job.location}</span>
                <span>{job.posted}</span>
              </div>

              <div className="job-right">
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    navigate(`/careers/${job.jobKey || job.id}`);
                  }}
                >
                  View details
                </button>
              </div>

            </div>
          ))}
        </div>

        {(canLoadMore || isLoadingMore) && (
          <div className="careers-loadWrap">
            <button
              type="button"
              className="careers-loadBtn"
              onClick={handleLoadMore}
              disabled={isLoadingMore}
            >
              {isLoadingMore ? (
                <>
                  <span className="careers-spinner" aria-hidden />
                  <span>Loading...</span>
                </>
              ) : (
                <span>Load more</span>
              )}
            </button>
          </div>
        )}

      </div>
    </section>
  );
};

export default CareersOpenPositions;